import cloneDeep from 'lodash/cloneDeep'
import countBy from 'lodash/countBy'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import { eachDay } from 'date-fns'
import dayjs from 'dayjs'
import { runInBatches } from '@/models/runInBatches'
import { CreativeAttachTargets } from '@/models/CampaignOperation/Constants'
import getBannersCacheable from './getBannersCacheable'
import { getRangeNumbers } from '@/models/GetRangeNumbers'

const getBanner = getBannersCacheable()

/** クリエイティブIDの配列からBannerSizeCodeを取得する */
const getBannerSizeCodes = async creativeIds => {
  const defaultSizeList = ['320x50', '300x250']

  // IDの重複を取り除く
  const ids = uniq(creativeIds)

  // バナーを取得し、bannerSizeCodeの型に変換する
  const banners = await runInBatches(ids.map(id => () => getBanner(id)))
  const sizes = banners.map(({ item }) => `${item.width}x${item.height}`)

  // バナーサイズの重複を取り除く
  const uniqueSizes = uniq(sizes)

  // '320x50'、'300x250'以外のサイズを除外
  const bannerSizeCodes = uniqueSizes.filter(size =>
    defaultSizeList.includes(size),
  )

  // '320x50'または'300x250'が存在しない場合はデフォルトの['320x50','300x250']を返す
  return !bannerSizeCodes.length ? defaultSizeList : bannerSizeCodes
}

const getCreativeIdsFromCampaign = async campaign => {
  return campaign.useCreativeSchedule
    ? campaign.campaignBudgetsCreatives
        // bannerのみsimulationする
        .filter(creative => creative.creativeType === 'banner')
        .map(creative => creative.id)
    : campaign.creatives
        // bannerのみsimulationする
        .filter(creative => creative.creativeType === 'banner')
        .map(creative => creative.id)
}

const getCreativeIdsFromDeliveryGroup = async groups => {
  return (
    groups
      // bannerのみsimulationする
      .filter(creative => creative.creativeType === 'banner')
      .map(group => group.creatives.map(creative => creative.id))
      .flat()
  )
}

const getDeliveryScheduleValues = campaignForm => {
  const weeklyDeliverySchedule =
    campaignForm.campaign.advancedSetting.weeklyDeliverySchedule
  const dayNumbers = getDayNumbers(campaignForm.campaignBudgets)
  const totalByDay = getTotalByDay(dayNumbers)
  const hourByDay = getHourByDay(weeklyDeliverySchedule)

  const allHours = getAllHours(hourByDay, totalByDay)
  const deliveryDaysSpecificHour = getSpecificHour(allHours)
  const deliveryDays = dayNumbers.length
  return { deliveryDaysSpecificHour, deliveryDays }
}

/** 開始・終了期間内の曜日番号を返す */
const getDayNumbers = campaignBudgets => {
  return campaignBudgets
    .filter(budget => dayjs().isBefore(dayjs(budget.endTime)))
    .map(budget => ({ ...budget, startTime: getStartTime(budget.startTime) }))
    .map(({ startTime, endTime }) => {
      return getDays(new Date(startTime), new Date(endTime))
    })
    .flat()
}

/** 開始時刻が現在よりも前の場合は現在時間を返す */
const getStartTime = startTime => {
  return dayjs().isBefore(dayjs(startTime)) ? startTime : new Date()
}

/** 開始日、終了日を受け取り、その期間内の曜日番号を返す */
const getDays = (start, end) => {
  const dateList = eachDay(start, end)
  return dateList.map(date => dayjs(date).day())
}

/** 配信期間内に曜日が何個づつあるかを返す */
const getTotalByDay = days => {
  const defaultTotalByDay = {
    sunday: 0,
    monday: 0,
    thursday: 0,
    wednesday: 0,
    tuesday: 0,
    friday: 0,
    saturday: 0,
  }
  return days.reduce((acc, cur) => {
    switch (cur) {
      case 0:
        acc.sunday++
        return acc
      case 1:
        acc.monday++
        return acc
      case 2:
        acc.tuesday++
        return acc
      case 3:
        acc.wednesday++
        return acc
      case 4:
        acc.thursday++
        return acc
      case 5:
        acc.friday++
        return acc
      case 6:
        acc.saturday++
        return acc
    }
    return acc
  }, defaultTotalByDay)
}

const getHourByDay = weeklyDeliverySchedule => {
  return groupBy(weeklyDeliverySchedule, 'day')
}

/** x曜日の配信時間帯 * 配信期間中のx曜日数を足し合わせる */
const getAllHours = (hourByDay, totalByDay) => {
  return Object.entries(hourByDay)
    .map(([key, value]) => {
      const total = totalByDay[key]
      return value
        .flat()
        .map(({ startHour, endHour }) => getRangeNumbers(startHour, endHour))
        .map(n => new Array(total).fill(n))
    })
    .flat(Infinity)
}

/** getAllHourで取得した期間内全時間帯をhourごとに集計 */
const getSpecificHour = allHours => {
  const countedHour = countBy(allHours)
  return Object.entries(countedHour).map(([hour, days]) => {
    return {
      hour: Number(hour),
      days,
    }
  })
}

const translateCampaignToSimulation = async (campaignForm, advertiser) => {
  /**
   * 変換時にコピー元に影響を及ぼさないようにディープコピーしておく
   */
  const clonedCampaignForm = cloneDeep(campaignForm)
  const [menuSetting, simulationPlan] = await Promise.all([
    translateMenuSetting(clonedCampaignForm.campaign),
    translateSimulationPlan(clonedCampaignForm),
  ])

  simulationPlan.advertiserId = advertiser.id
  menuSetting.simulationLocationGroups = translateSimulationLocationGroups(
    clonedCampaignForm,
  )

  return {
    menuSetting,
    simulationPlan,
  }
}

const translateSimulationLocationGroups = ({ deliveryLocationGroups }) => {
  return deliveryLocationGroups
    .filter(group => group.accessibleType === 'public')
    .map(group => {
      const { locationGroupId, name, radius, simulationLocations } = group
      return {
        id: locationGroupId,
        name,
        radius,
        status: 'active',
        simulationLocations: translateSimulationLocation(simulationLocations),
      }
    })
}

const translateSimulationLocation = simulationLocations => {
  return simulationLocations.map(location => {
    const { id, name = '', shape, shapeType, status = 'active' } = location
    return {
      id,
      name,
      shapeType,
      status,
      shape: {
        type: 'Point',
        coordinates: [shape.longitude, shape.latitude],
      },
    }
  })
}

const translateMenuSetting = async clonedCampaignForm => {
  const {
    enableBulkSetting,
    radius,
    minutesAgo,
    visitCount,
    target,
    useS2,
  } = clonedCampaignForm.menuSetting

  return {
    targetCode: target,
    enableBulkSetting,
    radius,
    useLowQualityLocation: useS2,
    minutesAgo,
    visitCount,
  }
}

const translateSimulationPlan = async clonedCampaignForm => {
  const { campaign, deliveryLocationGroups } = clonedCampaignForm
  const {
    creativeType,
    creativeAttachTarget,
    useAdvancedSetting,
    menu,
  } = campaign
  const {
    deliveryDevices,
    deliveryInventories,
    onlyJapaneseDevice,
  } = campaign.advancedSetting
  const {
    sex,
    marriage,
    age,
    prefecture,
  } = campaign.advancedSetting.demographicSetting

  const creativeIds =
    creativeAttachTarget === CreativeAttachTargets.CAMPAIGN
      ? await getCreativeIdsFromCampaign(campaign)
      : await getCreativeIdsFromDeliveryGroup(deliveryLocationGroups)

  const bannerSizeCode = await getBannerSizeCodes(uniq(creativeIds))
  const { deliveryDaysSpecificHour, deliveryDays } = getDeliveryScheduleValues(
    clonedCampaignForm,
  )

  const grossPrive =
    menu.unitType !== 'cost plus margin' ? menu.grossPrice : undefined

  const costPlusMarginPercent =
    menu.unitType === 'cost plus margin'
      ? menu.costPlusMarginPercentage
      : undefined

  return {
    creativeType,
    creativeSetting: {
      bannerSizeCode,
    },
    simulationMenu: {
      menuName: menu.name,
      menuType: menu.menuType,
      unitType: menu.unitType,
      grossPrice: grossPrive,
      costPlusMarginPercent: costPlusMarginPercent,
      deliveryTargets: [],
      currency: menu.currency,
    },
    useAdvancedSetting,
    deliveryDays,
    advancedSetting: {
      deliveryDevices,
      deliveryInventories,
      onlyJapaneseDevice: !!onlyJapaneseDevice,
      deliveryDaysSpecificHour,
      sex,
      marriage,
      age,
      prefecture,
    },
  }
}

export default translateCampaignToSimulation
